import React, { useEffect, useMemo, useState } from "react";
import { X, Download, Share2, Copy, Instagram, RefreshCw } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ShareLineupCard({ open, onClose, lineupId }) {
  const [format, setFormat] = useState("story"); // story | square
  const [bust, setBust] = useState(0);
  const [blob, setBlob] = useState(null);

  const url = useMemo(() => {
    if (!lineupId) return null;
    return `${API_BASE}/lineups/${lineupId}/card.png?format=${format}&v=${bust}`;
  }, [lineupId, format, bust]);

  // Cache the image as a blob for reuse (download/share)
  useEffect(() => {
    if (!url) return;
    let canceled = false;
    (async () => {
      try {
        const res = await fetch(url, { credentials: "include" });
        if (!res.ok) throw new Error("fetch failed");
        const b = await res.blob();
        if (!canceled) setBlob(b);
      } catch (e) {
        if (!canceled) setBlob(null);
      }
    })();
    return () => { canceled = true; };
  }, [url]);

  const download = () => {
    if (!blob) return;
    const link = document.createElement("a");
    const objectUrl = URL.createObjectURL(blob);
    link.href = objectUrl;
    link.download = `fanpuff-lineup-${lineupId}-${format}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
    toast.success("Downloaded. Share it on your story 🔥");
  };

  const doWebShare = async () => {
    if (!blob) return;
    try {
      const file = new File([blob], `fanpuff-lineup.png`, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: "My Fanpuff Lineup",
          text: "Pick stars. Win Form Points. — my Circle on Fanpuff.",
        });
      } else {
        // Fallback: copy link
        await navigator.clipboard.writeText(url);
        toast.success("Card link copied. Paste anywhere.");
      }
    } catch (e) {
      // user-cancel is fine
    }
  };

  const copyLink = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Card URL copied");
    } catch {
      toast.error("Could not copy");
    }
  };

  if (!open || !lineupId) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 grid place-items-center p-4"
        data-testid="share-lineup-dialog"
      >
        <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={onClose} />
        <motion.div
          initial={{ y: 14, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="relative w-full max-w-md glass-strong rounded-3xl p-5 border border-white/10"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10px] tracking-[0.3em] uppercase text-white/50 font-bold">Share</p>
              <h3 className="font-display text-2xl font-bold">Your lineup card</h3>
            </div>
            <button
              onClick={onClose}
              data-testid="share-close"
              className="w-9 h-9 grid place-items-center rounded-full hover:bg-white/10"
              aria-label="close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Format toggle */}
          <div className="mt-3 flex gap-2">
            {[{ v: "story", l: "9:16 · Story", icon: Instagram }, { v: "square", l: "1:1 · Post", icon: Share2 }].map(t => (
              <button
                key={t.v}
                data-testid={`share-format-${t.v}`}
                onClick={() => setFormat(t.v)}
                className="chip px-3 py-1.5 text-xs inline-flex items-center gap-1.5"
                style={format === t.v
                  ? { background: "linear-gradient(90deg,#B026FF,#00F0FF)", color: "#000", border: "1px solid transparent" }
                  : { background: "rgba(255,255,255,0.05)", color: "#fff", border: "1px solid rgba(255,255,255,0.1)" }}
              >
                <t.icon className="w-3 h-3" /> {t.l}
              </button>
            ))}
            <button
              onClick={() => setBust(b => b + 1)}
              data-testid="share-refresh"
              className="ml-auto w-9 h-9 grid place-items-center rounded-full hover:bg-white/10"
              aria-label="refresh"
              title="Refresh preview"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>

          {/* Preview */}
          <div className="mt-3 rounded-2xl overflow-hidden bg-black/50 border border-white/10 grid place-items-center p-3">
            {url ? (
              <img
                key={url}
                src={url}
                alt="Lineup card preview"
                className={`max-h-[460px] w-auto ${format === "story" ? "aspect-[9/16]" : "aspect-square"} rounded-xl shadow-xl`}
                style={{ objectFit: "contain" }}
                data-testid="share-preview-img"
              />
            ) : (
              <div className="h-64 flex items-center justify-center text-white/60 text-sm">Generating preview…</div>
            )}
          </div>

          {/* Actions */}
          <div className="mt-4 grid grid-cols-3 gap-2">
            <button
              onClick={download}
              data-testid="share-download"
              className="py-3 rounded-full text-black font-bold text-sm inline-flex items-center justify-center gap-1.5"
              style={{ background: "linear-gradient(90deg,#00F0FF,#B026FF)" }}
            >
              <Download className="w-4 h-4" /> Save
            </button>
            <button
              onClick={doWebShare}
              data-testid="share-native"
              className="py-3 rounded-full font-bold text-sm inline-flex items-center justify-center gap-1.5 glass hover:bg-white/10"
            >
              <Share2 className="w-4 h-4" /> Share
            </button>
            <button
              onClick={copyLink}
              data-testid="share-copy"
              className="py-3 rounded-full font-bold text-sm inline-flex items-center justify-center gap-1.5 glass hover:bg-white/10"
            >
              <Copy className="w-4 h-4" /> Copy link
            </button>
          </div>

          <p className="mt-3 text-[10px] text-white/40 leading-relaxed">
            Card image is public and shareable. Fan Credits and Fanpuff XP have no cash value.
          </p>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
